import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '@/lib/firebase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { Badge } from '@/components/ui/badge';
import {
  isPaidSongRequestForLedger,
  isPaidSongRequestForArtistLedger,
  payoutDocMatchesArtistRail,
  payoutSourceOf,
  MIN_MANUAL_PAYOUT_GBP,
  MIN_MANUAL_PAYOUT_USD,
  applyAdminLedgerDisplay,
  ledgerCurrencyForRequest,
} from '@/lib/payoutLedger';
import AdminCollapsibleSection from '@/features/admin/components/AdminCollapsibleSection';
import { Pencil, Check, X } from 'lucide-react';
import { formatUkDateTime } from '@/lib/adminFormat';
import { formatCurrency, getCurrencyForCountry } from '@/lib/currency';

const formatGbp = (value) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(value || 0));
const formatUsd = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value || 0));

const formatDate = (value) => formatUkDateTime(value);

const AdminFinancialPanel = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [profilesMap, setProfilesMap] = useState({});
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [notes, setNotes] = useState({});
  const [references, setReferences] = useState({});
  const [updatingId, setUpdatingId] = useState(null);
  const [editingArtistId, setEditingArtistId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [editSnapshot, setEditSnapshot] = useState(null);
  const [savingBalanceId, setSavingBalanceId] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [requestsSnap, payoutSnap, profilesSnap] = await Promise.all([
        getDocs(collection(db, 'requests')),
        getDocs(query(collection(db, 'artist_payout_requests'), orderBy('requested_at', 'desc'))),
        getDocs(collection(db, 'profiles')),
      ]);

      setRequests(requestsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setPayoutRequests(payoutSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      const map = {};
      profilesSnap.docs.forEach((d) => {
        map[d.id] = d.data();
      });
      setProfilesMap(map);
    } catch (error) {
      console.error('Error loading admin financial data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load financial data.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const summary = useMemo(() => {
    const paidReqs = requests.filter(isPaidSongRequestForLedger);
    let gbpGross = 0;
    let usdGross = 0;
    const extraGross = {};
    paidReqs.forEach((req) => {
      const totalPaid = Number(req.total_paid);
      const amt =
        !Number.isNaN(totalPaid) && totalPaid > 0 ? totalPaid : Number(req.amount_paid || 0);
      if (Number.isNaN(amt)) return;
      const cur = ledgerCurrencyForRequest(req);
      if (cur === 'USD') usdGross += amt;
      else if (cur === 'GBP') gbpGross += amt;
      else extraGross[cur] = (extraGross[cur] || 0) + amt;
    });

    const fold = (gross) => ({
      grossRevenue: gross,
      artistShare: gross * 0.7,
      platformShare: gross * 0.3,
    });

    const zeroPad = { pendingAmount: 0, paidAmount: 0, pendingCount: 0 };
    const gbp = { ...fold(gbpGross), ...zeroPad };
    const usd = { ...fold(usdGross), ...zeroPad };

    payoutRequests.forEach((r) => {
      const amt = Number(r.amount || 0);
      const st = r.status;
      const isUsdP = String(r.currency || 'GBP').toUpperCase() === 'USD';
      const target = isUsdP ? usd : gbp;
      if (st === 'pending' || st === 'approved') {
        target.pendingAmount += amt;
        if (st === 'pending') target.pendingCount += 1;
      }
      if (st === 'paid') target.paidAmount += amt;
    });

    return { gbp, usd, extraGross };
  }, [requests, payoutRequests]);

  const artistBalanceSheet = useMemo(() => {
    const grossByArtist = {};

    requests.forEach((req) => {
      const artistId = req.artist_id;
      if (!artistId) return;
      const profile = profilesMap[artistId] || {};
      if (!isPaidSongRequestForArtistLedger(req, profile)) return;
      const totalPaid = Number(req.total_paid);
      const amount = !Number.isNaN(totalPaid) && totalPaid > 0 ? totalPaid : Number(req.amount_paid || 0);
      grossByArtist[artistId] = (grossByArtist[artistId] || 0) + (Number.isNaN(amount) ? 0 : amount);
    });

    const payoutByArtist = {};
    const reservedByArtist = {};
    payoutRequests.forEach((payout) => {
      if (payoutSourceOf(payout) === 'karaoke') return;
      const artistId = payout.artist_id;
      if (!artistId) return;
      const profile = profilesMap[artistId] || {};
      if (!payoutDocMatchesArtistRail(payout, profile)) return;
      const amount = Number(payout.amount || 0);
      if (Number.isNaN(amount) || amount <= 0) return;
      if (payout.status === 'paid') {
        payoutByArtist[artistId] = (payoutByArtist[artistId] || 0) + amount;
      }
      if (payout.status === 'pending' || payout.status === 'approved') {
        reservedByArtist[artistId] = (reservedByArtist[artistId] || 0) + amount;
      }
    });

    const artistIds = Array.from(
      new Set([
        ...Object.keys(profilesMap),
        ...Object.keys(grossByArtist),
        ...Object.keys(payoutByArtist),
        ...Object.keys(reservedByArtist),
      ])
    );

    return artistIds
      .map((artistId) => {
        const profile = profilesMap[artistId] || {};
        const isUsArtist = String(profile.country || '').toUpperCase() === 'US';
        const gross = grossByArtist[artistId] || 0;
        const artistShare = Number((gross * 0.7).toFixed(2));
        const paidOutDoc = Number(payoutByArtist[artistId] || 0);
        const paidOut = isUsArtist
          ? paidOutDoc
          : Number(paidOutDoc || profile.payouts_paid_total || 0);
        const reservedLive = Number(reservedByArtist[artistId] || 0);
        const live = {
          gross,
          artistShare,
          paidOut,
          reserved: reservedLive,
        };
        const display = applyAdminLedgerDisplay(live, profile);
        return {
          artistId,
          artistName: profile.artist_name || profile.karaoke_name || profile.email || artistId,
          isUsArtist,
          artistCountry: String(profile.country || 'GB').toUpperCase(),
          ledgerCurrency: getCurrencyForCountry(profile.country),
          ...display,
        };
      })
      .sort((a, b) => b.currentBalance - a.currentBalance || b.artistShare - a.artistShare);
  }, [profilesMap, payoutRequests, requests]);

  const updatePayoutStatus = async (requestId, status) => {
    setUpdatingId(requestId);
    try {
      const payload = {
        requestId,
        status,
        adminNote: notes[requestId] || '',
        paidReference: references[requestId] || '',
      };

      // Primary path: same-origin HTTP endpoint (no cross-origin preflight).
      try {
        const endpoint = '/api/admin/payouts/update';
        const idToken = await auth.currentUser?.getIdToken?.();
        if (!idToken) throw new Error('Missing auth token');
        const resp = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify(payload),
        });

        if (!resp.ok) {
          const text = await resp.text();
          throw new Error(text || `HTTP ${resp.status}`);
        }
      } catch (httpError) {
        // Fallback path: callable in case hosting rewrite is stale/cached.
        const fn = httpsCallable(getFunctions(auth.app, 'us-central1'), 'adminUpdateArtistPayoutRequest');
        await fn(payload);
      }

      toast({
        title: 'Updated',
        description: `Payout request marked as ${status}.`,
      });

      await loadData();
    } catch (error) {
      console.error('Error updating payout status:', error);
      toast({
        title: 'Update failed',
        description: error.message || 'Could not update payout request.',
        variant: 'destructive',
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const moneyFields = ['gross', 'artistShare', 'paidOut', 'reserved', 'currentBalance'];

  const startEditRow = (row) => {
    const snapshot = {
      gross: row.gross,
      artistShare: row.artistShare,
      paidOut: row.paidOut,
      reserved: row.reserved,
      currentBalance: row.currentBalance,
    };
    setEditingArtistId(row.artistId);
    setEditSnapshot(snapshot);
    setEditDraft({
      gross: snapshot.gross.toFixed(2),
      artistShare: snapshot.artistShare.toFixed(2),
      paidOut: snapshot.paidOut.toFixed(2),
      reserved: snapshot.reserved.toFixed(2),
      currentBalance: snapshot.currentBalance.toFixed(2),
    });
  };

  const cancelEditRow = () => {
    setEditingArtistId(null);
    setEditDraft(null);
    setEditSnapshot(null);
  };

  const saveEditRow = async (row) => {
    const fields = {};
    for (const key of moneyFields) {
      const desired = Number(editDraft?.[key]);
      if (!Number.isFinite(desired) || desired < 0) {
        toast({
          title: 'Invalid amount',
          description: 'Enter 0 or more in every field you change.',
          variant: 'destructive',
        });
        return;
      }
      if (Math.abs(desired - Number(editSnapshot?.[key] || 0)) >= 0.005) {
        fields[key] = Number(desired.toFixed(2));
      }
    }
    if (Object.keys(fields).length === 0) {
      cancelEditRow();
      return;
    }
    setSavingBalanceId(row.artistId);
    try {
      const fn = httpsCallable(getFunctions(auth.app, 'us-central1'), 'adminSetArtistCurrentBalance');
      await fn({ artistId: row.artistId, fields });
      const changed = Object.keys(fields).length;
      toast({
        title: 'Ledger updated',
        description: `Saved ${changed} field${changed === 1 ? '' : 's'} for ${row.artistName}.`,
      });
      cancelEditRow();
      await loadData();
    } catch (error) {
      console.error('Error saving artist ledger:', error);
      toast({
        title: 'Could not save',
        description: error.message || 'Try again in a moment.',
        variant: 'destructive',
      });
    } finally {
      setSavingBalanceId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AdminCollapsibleSection
        title="Manual Payout Requests"
        description="Approve, pay or reject artist and karaoke payouts"
        defaultOpen={true}
      >
        <div className="space-y-3">
          <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-3 text-xs text-gray-300 space-y-2">
            <p>
              Each row is tagged <strong className="text-white">Artist</strong> or{' '}
              <strong className="text-white">Karaoke</strong> — use the same approve / paid / reject flow. Karaoke balances
              come only from karaoke paid requests; keep them separate from artist earnings for the same account.
            </p>
            <p>
              <strong className="text-white">UK / IE (GBP)</strong> — bank transfer. Minimum available{' '}
              {formatGbp(MIN_MANUAL_PAYOUT_GBP)} before requesting. Sort code &amp; account number shown below.
            </p>
            <p>
              <strong className="text-white">US (USD)</strong> — PayPal or Venmo only. Minimum available{' '}
              {formatUsd(MIN_MANUAL_PAYOUT_USD)}. No UK bank fields.
            </p>
            <p>
              Workflow: <strong className="text-white">Pending</strong> → <strong className="text-white">Approved</strong>{' '}
              → <strong className="text-white">Paid</strong> or <strong className="text-white">Rejected</strong>. Users
              are notified in-app and by email.
            </p>
          </div>
          {payoutRequests.length === 0 ? (
            <p className="text-gray-400 text-sm">No payout requests yet.</p>
          ) : (
            payoutRequests.map((item) => {
              const profile = profilesMap[item.artist_id] || {};
              const src = payoutSourceOf(item);
              const isUsdPayout = String(item.currency || 'GBP').toUpperCase() === 'USD';
              const isApproved = item.status === 'approved';
              const fmtAmt = isUsdPayout ? formatUsd : formatGbp;
              const statusColor =
                item.status === 'paid'
                  ? 'border-green-500 text-green-300'
                  : item.status === 'approved'
                  ? 'border-blue-500 text-blue-300'
                  : item.status === 'rejected'
                  ? 'border-red-500 text-red-300'
                  : 'border-yellow-500 text-yellow-300';

              return (
                <div key={item.id} className="p-4 rounded-lg bg-gray-900/60 border border-gray-700 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-white font-semibold">{fmtAmt(item.amount)}</p>
                        <Badge
                          variant="outline"
                          className={
                            src === 'karaoke'
                              ? 'border-fuchsia-500 text-fuchsia-200 text-[10px]'
                              : 'border-slate-500 text-slate-200 text-[10px]'
                          }
                        >
                          {src === 'karaoke' ? 'Karaoke host' : 'Artist'}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500">{isUsdPayout ? 'USD — PayPal / Venmo' : 'GBP — UK bank'}</p>
                      <p className="text-sm text-gray-300">
                        {profile.artist_name || profile.karaoke_name || profile.email || item.artist_id}
                      </p>
                      <p className="text-xs text-gray-400">Requested: {formatDate(item.requested_at)}</p>
                    </div>
                    <Badge variant="outline" className={statusColor}>
                      {item.status || 'pending'}
                    </Badge>
                  </div>

                  {isUsdPayout ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      <div>
                        <p className="text-xs text-gray-500">Method</p>
                        <p className="text-gray-200 capitalize">{item.payout_method || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">
                          {item.payout_method === 'venmo' ? 'Venmo username' : 'PayPal email'}
                        </p>
                        <p className="text-gray-200 break-all">
                          {item.payout_method === 'venmo'
                            ? item.venmo_handle
                              ? `@${item.venmo_handle}`
                              : 'N/A'
                            : item.paypal_email || 'N/A'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                      <div>
                        <p className="text-xs text-gray-500">Account Name</p>
                        <p className="text-gray-200">{item.bank_account_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Sort Code</p>
                        <p className="text-gray-200">{item.bank_sort_code || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Account Number</p>
                        <p className="text-gray-200">{item.bank_account_number || 'N/A'}</p>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <Input
                      placeholder="Admin note"
                      value={notes[item.id] || ''}
                      onChange={(e) => setNotes((prev) => ({ ...prev, [item.id]: e.target.value }))}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                    <Input
                      placeholder="Paid reference (optional)"
                      value={references[item.id] || ''}
                      onChange={(e) => setReferences((prev) => ({ ...prev, [item.id]: e.target.value }))}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>

                  {item.status !== 'paid' && item.status !== 'rejected' && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className={
                          isApproved
                            ? 'bg-gray-600 text-gray-200 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }
                        disabled={updatingId === item.id || isApproved}
                        onClick={() => updatePayoutStatus(item.id, 'approved')}
                      >
                        Approve (Next Working Day)
                      </Button>
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        disabled={updatingId === item.id}
                        onClick={() => updatePayoutStatus(item.id, 'paid')}
                      >
                        Mark Paid
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={updatingId === item.id}
                        onClick={() => updatePayoutStatus(item.id, 'rejected')}
                      >
                        Reject
                      </Button>
                    </div>
                  )}

                  {item.processed_at && (
                    <p className="text-xs text-gray-400">Processed: {formatDate(item.processed_at)}</p>
                  )}
                </div>
              );
            })
          )}
        </div>
      </AdminCollapsibleSection>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4">
            <p className="text-xs text-gray-400">Gross Revenue</p>
            <p className="text-xl font-bold text-white">{formatGbp(summary.gbp.grossRevenue)}</p>
            <p className="text-sm font-semibold text-sky-300 mt-1">{formatUsd(summary.usd.grossRevenue)}</p>
            <p className="text-[10px] text-gray-500 mt-0.5">GBP / USD</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4">
            <p className="text-xs text-gray-400">Artist Share (70%)</p>
            <p className="text-xl font-bold text-green-300">{formatGbp(summary.gbp.artistShare)}</p>
            <p className="text-sm font-semibold text-green-200/90 mt-1">{formatUsd(summary.usd.artistShare)}</p>
            <p className="text-[10px] text-gray-500 mt-0.5">GBP / USD</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4">
            <p className="text-xs text-gray-400">Platform Share (30%)</p>
            <p className="text-xl font-bold text-purple-300">{formatGbp(summary.gbp.platformShare)}</p>
            <p className="text-sm font-semibold text-purple-200/90 mt-1">{formatUsd(summary.usd.platformShare)}</p>
            <p className="text-[10px] text-gray-500 mt-0.5">GBP / USD</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4">
            <p className="text-xs text-gray-400">Pending Payout Requests</p>
            <p className="text-xl font-bold text-yellow-300">{formatGbp(summary.gbp.pendingAmount)}</p>
            <p className="text-sm font-semibold text-amber-200/90 mt-1">{formatUsd(summary.usd.pendingAmount)}</p>
            <p className="text-[10px] text-gray-500 mt-0.5">
              {summary.gbp.pendingCount + summary.usd.pendingCount} request(s)
            </p>
          </CardContent>
        </Card>
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4">
            <p className="text-xs text-gray-400">Paid Out (Logged)</p>
            <p className="text-xl font-bold text-blue-300">{formatGbp(summary.gbp.paidAmount)}</p>
            <p className="text-sm font-semibold text-sky-200/90 mt-1">{formatUsd(summary.usd.paidAmount)}</p>
            <p className="text-[10px] text-gray-500 mt-0.5">GBP / USD</p>
          </CardContent>
        </Card>
      </div>
      {summary.extraGross && Object.keys(summary.extraGross).length > 0 ? (
        <p className="text-xs text-gray-400">
          Other PayPal currencies (gross):{' '}
          {Object.entries(summary.extraGross)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([code, amt]) => `${formatCurrency(amt, code)}`)
            .join(' · ')}
        </p>
      ) : null}

      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white">Artist Balance Sheet</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {artistBalanceSheet.length === 0 ? (
            <p className="text-gray-400 text-sm">No artist balances to show yet.</p>
          ) : (
            <div className="space-y-2">
              {artistBalanceSheet.map((row) => {
                const fmt = (v) => formatCurrency(Number(v || 0), row.ledgerCurrency || 'GBP');
                const isEditing = editingArtistId === row.artistId;
                const saving = savingBalanceId === row.artistId;
                const renderMoney = (label, field, valueClass = 'text-gray-200') => (
                  <div>
                    <p className="text-xs text-gray-500">{label}</p>
                    {isEditing ? (
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={editDraft?.[field] ?? ''}
                        onChange={(e) => setEditDraft((prev) => ({ ...prev, [field]: e.target.value }))}
                        className="h-8 mt-1 bg-gray-800 border-gray-600 text-white text-sm px-2"
                        disabled={saving}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEditRow(row);
                          if (e.key === 'Escape') cancelEditRow();
                        }}
                      />
                    ) : (
                      <p className={`text-sm ${valueClass}`}>{fmt(row[field])}</p>
                    )}
                  </div>
                );
                return (
                  <div
                    key={row.artistId}
                    className="grid grid-cols-1 md:grid-cols-6 gap-2 p-3 rounded-lg bg-gray-900/60 border border-gray-700"
                  >
                    <div>
                      <p className="text-xs text-gray-500">Artist</p>
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-gray-100 text-sm font-medium truncate">{row.artistName}</p>
                        {!isEditing && (
                          <button
                            type="button"
                            onClick={() => startEditRow(row)}
                            className="text-gray-400 hover:text-white p-0.5 rounded flex-shrink-0"
                            aria-label={`Edit ledger for ${row.artistName}`}
                            title="Edit figures"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <p className="text-[10px] text-gray-500 mt-0.5">
                        {row.artistCountry} · {row.ledgerCurrency}
                      </p>
                      {row.hasAnyEdit && !isEditing && (
                        <p className="text-[10px] text-amber-400/90 mt-0.5">Includes admin edit</p>
                      )}
                      {isEditing && (
                        <div className="flex items-center gap-1 mt-2">
                          <Button
                            type="button"
                            size="sm"
                            className="h-7 px-2 bg-green-600 hover:bg-green-700"
                            disabled={saving}
                            onClick={() => saveEditRow(row)}
                          >
                            <Check className="w-3.5 h-3.5 mr-1" />
                            Save
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 border-gray-600"
                            disabled={saving}
                            onClick={cancelEditRow}
                          >
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                    {renderMoney('Total Earned', 'gross')}
                    {renderMoney('Artist Share', 'artistShare')}
                    {renderMoney('Paid Out', 'paidOut')}
                    {renderMoney('Reserved', 'reserved')}
                    {renderMoney('Current Balance', 'currentBalance', 'text-green-300 font-semibold')}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminFinancialPanel;
